import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DEVICE_TYPES } from "./constants";

function emptyData() {
  return {
    customer: "",
    code: "",
    phone: "",
    description: "",
    notes: "",
    charge: "",
    delivery_date: "",
    device_type: "",
    power_cable: false,
    connection_cables: false,
    no_accessories: false,
    with_box: false,
    work_done: "",
    notify_tel: false,
    notify_mail: false,
    notify_store: false,
    notify_onsite: false,
    notify_at: "",
  };
}

// Local datetime "YYYY-MM-DDTHH:mm" for <input type="datetime-local"> default = now
function nowLocalISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function NoteDialog({
  open,
  onOpenChange,
  mode,
  initial,
  defaultType,
  types,
  channels,
  people,
  onSubmit,
}) {
  const [type, setType] = useState(defaultType || "task");
  const [data, setData] = useState(emptyData());
  const [selectedChannel, setSelectedChannel] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [workDoneOpen, setWorkDoneOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [orderItems, setOrderItems] = useState([]); // [{text, qty, delivered, done}]

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setType(initial.type);
      // For "order" type with existing items array, populate description with item texts
      let descValue = initial?.data?.description || "";
      if (
        initial.type === "order" &&
        Array.isArray(initial?.data?.items) &&
        initial.data.items.length > 0
      ) {
        descValue = initial.data.items.map((i) => i.text).join("\n");
      }
      // Order items list (with qty)
      const baseItems = Array.isArray(initial?.data?.items)
        ? initial.data.items
        : [];
      setOrderItems(
        initial.type === "order"
          ? baseItems.map((it) => ({
              text: it.text || "",
              qty: Math.max(1, parseInt(it.qty ?? 1, 10) || 1),
              delivered: Math.max(0, parseInt(it.delivered ?? 0, 10) || 0),
              done: !!it.done,
            }))
          : []
      );
      setData({
        customer: initial?.data?.customer || "",
        code: initial?.data?.code || "",
        phone: initial?.data?.phone || "",
        description: descValue,
        notes: initial?.data?.notes || "",
        charge: initial?.data?.charge || "",
        delivery_date: initial?.data?.delivery_date
          ? new Date(initial.data.delivery_date).toISOString().slice(0, 10)
          : "",
        device_type: initial?.data?.device_type || "",
        power_cable: !!initial?.data?.power_cable,
        connection_cables: !!initial?.data?.connection_cables,
        no_accessories: !!initial?.data?.no_accessories,
        with_box: !!initial?.data?.with_box,
        work_done: initial?.data?.work_done || "",
        notify_tel: !!initial?.data?.notify_tel,
        notify_mail: !!initial?.data?.notify_mail,
        notify_store: !!initial?.data?.notify_store,
        notify_onsite: !!initial?.data?.notify_onsite,
        notify_at: initial?.data?.notify_at
          ? new Date(initial.data.notify_at).toISOString().slice(0, 16)
          : "",
      });
      setSelectedChannel((initial.channels && initial.channels[0]) || "");
      setAssignedTo(initial.assigned_to || "");
      // Auto-open collapsibles if there's existing data
      setWorkDoneOpen(!!(initial?.data?.work_done || "").trim());
      setNotifyOpen(
        !!(
          initial?.data?.notify_tel ||
          initial?.data?.notify_mail ||
          initial?.data?.notify_store ||
          initial?.data?.notify_onsite
        )
      );
    } else {
      setType(defaultType || "task");
      setData(emptyData());
      setSelectedChannel("");
      setAssignedTo("");
      setWorkDoneOpen(false);
      setNotifyOpen(false);
      setOrderItems(
        defaultType === "order" ? [{ text: "", qty: "", delivered: 0, done: false }] : []
      );
    }
  }, [open, mode, initial, defaultType]);

  // When switching to "order" type with no existing rows, add an empty one
  useEffect(() => {
    if (type === "order" && orderItems.length === 0) {
      setOrderItems([{ text: "", qty: "", delivered: 0, done: false }]);
    }
  }, [type, orderItems.length]);

  const submit = (e) => {
    e.preventDefault();
    // Build items for "order" type from the 2-column editor
    let items = [];
    if (type === "order") {
      items = orderItems
        .map((it) => ({
          text: (it.text || "").trim(),
          qty: Math.max(1, parseInt(it.qty || 1, 10) || 1),
          delivered: Math.max(0, parseInt(it.delivered || 0, 10) || 0),
          done: !!it.done,
        }))
        .filter((it) => it.text.length > 0)
        .map((it) => ({
          ...it,
          // Cap delivered to qty; auto-flag done if fully delivered
          delivered: Math.min(it.delivered, it.qty),
          done: it.delivered >= it.qty ? true : it.done && it.delivered === it.qty,
        }));
    }
    const hasOrderItems = type === "order" && items.length > 0;
    // For order type, customer or items required (description is auto-derived)
    if (
      type === "order"
        ? !data.customer.trim() && !hasOrderItems
        : !data.customer.trim() && !data.description.trim()
    )
      return;
    if (!selectedChannel || !assignedTo) return;

    const supportsExtras = type === "pickup" || type === "task";
    const anyNotify =
      supportsExtras && notifyOpen &&
      (data.notify_tel || data.notify_mail || data.notify_store || data.notify_onsite);

    const payload = {
      type,
      data: {
        customer: data.customer.trim(),
        code: data.code.trim(),
        phone: data.phone.trim(),
        description:
          type === "order"
            ? items
                .map((it) => (it.qty > 1 ? `${it.qty}x ${it.text}` : it.text))
                .join("\n")
            : data.description.trim(),
        notes: type === "order" ? data.notes.trim() : "",
        charge: data.charge.trim(),
        delivery_date: data.delivery_date
          ? new Date(data.delivery_date).toISOString()
          : "",
        device_type: type === "pickup" ? data.device_type : "",
        power_cable: type === "pickup" ? !!data.power_cable : false,
        connection_cables: type === "pickup" ? !!data.connection_cables : false,
        no_accessories: type === "pickup" ? !!data.no_accessories : false,
        with_box: type === "pickup" ? !!data.with_box : false,
        items: type === "order" ? items : [],
        work_done:
          supportsExtras && workDoneOpen ? data.work_done.trim() : "",
        notify_tel: supportsExtras && notifyOpen ? !!data.notify_tel : false,
        notify_mail: supportsExtras && notifyOpen ? !!data.notify_mail : false,
        notify_store: supportsExtras && notifyOpen ? !!data.notify_store : false,
        notify_onsite: supportsExtras && notifyOpen ? !!data.notify_onsite : false,
        notify_at:
          anyNotify && data.notify_at
            ? new Date(data.notify_at).toISOString()
            : "",
      },
      channels: selectedChannel ? [selectedChannel] : [],
      note_date: null,
      assigned_to: assignedTo || null,
    };
    onSubmit(payload);
  };

  const selectedTypeMeta = types.find((t) => t.value === type) || types[0];

  const setField = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const hasOrderItem = orderItems.some((it) => (it.text || "").trim());
  const canSubmit =
    (data.customer.trim() ||
      (type === "order" ? hasOrderItem : data.description.trim())) &&
    !!selectedChannel &&
    !!assignedTo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[520px] rounded-2xl p-0 overflow-hidden border-none max-h-[92vh] overflow-y-auto"
        data-testid="note-dialog"
      >
        <div className="px-7 pt-6 pb-3" style={{ backgroundColor: selectedTypeMeta.color }}>
          <DialogHeader>
            <DialogTitle
              className="font-caveat text-3xl font-bold text-ink"
              data-testid="dialog-title"
            >
              {mode === "edit" ? "ΕΠΕΞΕΡΓΑΣΙΑ" : "ΝΕΑ ΚΑΤΑΧΩΡΗΣΗ"}
            </DialogTitle>
            <DialogDescription className="text-ink/70 text-[10px] uppercase tracking-[0.2em] font-bold">
              {selectedTypeMeta.label}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="px-7 pb-6 pt-4 bg-white space-y-3" data-testid="note-form">
          {/* TYPE */}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60">
              ΚΑΤΗΓΟΡΙΑ
            </Label>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {types.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-full transition-all flex items-center gap-1 border ${
                    type === t.value
                      ? "border-ink bg-ink text-white"
                      : "border-black/10 text-ink/65 hover:border-ink/40"
                  }`}
                  data-testid={`dialog-type-${t.value}`}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: t.color }}
                  />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* CUSTOMER */}
          <Row label="ΠΕΛΑΤΗΣ">
            <Input
              autoFocus
              value={data.customer}
              onChange={(e) => setField("customer", e.target.value)}
              className="rounded-xl bg-white/60 border-black/10 focus-visible:ring-ink/20 font-arial font-bold"
              data-testid="dialog-customer-input"
              placeholder="ΟΝΟΜΑ ΠΕΛΑΤΗ"
            />
          </Row>

          {/* CODE + PHONE (2 cols) */}
          <div className="grid grid-cols-2 gap-2">
            <Row label="ΚΩΔΙΚΟΣ / ΑΦΜ">
              <Input
                value={data.code}
                onChange={(e) => setField("code", e.target.value)}
                className="rounded-xl bg-white/60 border-black/10 focus-visible:ring-ink/20 font-arial"
                data-testid="dialog-code-input"
              />
            </Row>
            <Row label="ΤΗΛΕΦΩΝΟ">
              <Input
                value={data.phone}
                onChange={(e) => setField("phone", e.target.value)}
                className="rounded-xl bg-white/60 border-black/10 focus-visible:ring-ink/20 font-arial"
                data-testid="dialog-phone-input"
              />
            </Row>
          </div>

          {/* PICKUP-ONLY: device type droplist */}
          {type === "pickup" && (
            <Row label="ΤΥΠΟΣ">
              <select
                value={data.device_type}
                onChange={(e) => setField("device_type", e.target.value)}
                className="w-full rounded-xl bg-white/60 border border-black/10 focus-visible:ring-ink/20 font-arial font-bold px-3 py-2 text-sm"
                data-testid="dialog-device-type-select"
              >
                <option value="">— ΕΠΙΛΕΞΕ ΤΥΠΟ —</option>
                {DEVICE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Row>
          )}

          {/* DESCRIPTION (bigger) — label changes for pickup / order */}
          <Row
            label={
              type === "pickup"
                ? "ΠΕΡΙΓΡΑΦΗ ΒΛΑΒΗΣ"
                : type === "order"
                ? `ΕΙΔΗ ΠΑΡΑΓΓΕΛΙΑΣ (${orderItems.filter((i) => (i.text || "").trim()).length})`
                : "ΠΕΡΙΓΡΑΦΗ"
            }
          >
            {type === "order" ? (
              <OrderItemsEditor
                items={orderItems}
                onChange={setOrderItems}
              />
            ) : (
              <Textarea
                value={data.description}
                onChange={(e) => setField("description", e.target.value)}
                rows={5}
                className="rounded-xl bg-white/60 border-black/10 focus-visible:ring-ink/20 resize-none font-arial"
                data-testid="dialog-description-input"
                placeholder={
                  type === "pickup"
                    ? "ΠΕΡΙΓΡΑΦΗ ΒΛΑΒΗΣ / ΣΥΜΠΤΩΜΑΤΑ..."
                    : "ΠΛΗΡΕΣ ΚΕΙΜΕΝΟ / ΛΕΠΤΟΜΕΡΕΙΕΣ..."
                }
              />
            )}
          </Row>

          {/* ORDER-ONLY: free-text notes (additional info) */}
          {type === "order" && (
            <Row label="ΣΗΜΕΙΩΣΕΙΣ">
              <Textarea
                value={data.notes}
                onChange={(e) => setField("notes", e.target.value)}
                rows={3}
                className="rounded-xl bg-white/60 border-black/10 focus-visible:ring-ink/20 resize-none font-arial uppercase"
                data-testid="dialog-order-notes-input"
                placeholder="ΠΡΟΑΙΡΕΤΙΚΕΣ ΣΗΜΕΙΩΣΕΙΣ ΓΙΑ ΤΗΝ ΠΑΡΑΓΓΕΛΙΑ..."
              />
            </Row>
          )}

          {/* PICKUP-ONLY: cable checks */}
          {type === "pickup" && (
            <Row label="ΕΞΟΠΛΙΣΜΟΣ ΠΟΥ ΠΑΡΕΛΗΦΘΗ">
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-center gap-2 cursor-pointer text-xs font-bold uppercase tracking-wider select-none ${
                    data.no_accessories ? "text-red-700" : "text-ink/80"
                  }`}
                  data-testid="dialog-no-accessories-label"
                >
                  <input
                    type="checkbox"
                    checked={!!data.no_accessories}
                    onChange={(e) =>
                      setField("no_accessories", e.target.checked)
                    }
                    className="w-4 h-4 accent-red-600"
                    data-testid="dialog-no-accessories-check"
                  />
                  ΧΩΡΙΣ ΠΑΡΕΛΚΟΜΕΝΑ
                </label>
                <label
                  className="flex items-center gap-2 cursor-pointer text-xs font-bold uppercase tracking-wider text-ink/80 select-none"
                  data-testid="dialog-power-cable-label"
                >
                  <input
                    type="checkbox"
                    checked={!!data.power_cable}
                    onChange={(e) => setField("power_cable", e.target.checked)}
                    className="w-4 h-4 accent-ink"
                    data-testid="dialog-power-cable-check"
                  />
                  ΚΑΛΩΔΙΟ ΡΕΥΜΑΤΟΣ / ΑΝΤΑΠΤΟΡΑΣ
                </label>
                <label
                  className="flex items-center gap-2 cursor-pointer text-xs font-bold uppercase tracking-wider text-ink/80 select-none"
                  data-testid="dialog-connection-cables-label"
                >
                  <input
                    type="checkbox"
                    checked={!!data.connection_cables}
                    onChange={(e) =>
                      setField("connection_cables", e.target.checked)
                    }
                    className="w-4 h-4 accent-ink"
                    data-testid="dialog-connection-cables-check"
                  />
                  ΚΑΛΩΔΙΑ ΣΥΝΔΕΣΗΣ
                </label>
                <label
                  className="flex items-center gap-2 cursor-pointer text-xs font-bold uppercase tracking-wider text-ink/80 select-none"
                  data-testid="dialog-with-box-label"
                >
                  <input
                    type="checkbox"
                    checked={!!data.with_box}
                    onChange={(e) => setField("with_box", e.target.checked)}
                    className="w-4 h-4 accent-ink"
                    data-testid="dialog-with-box-check"
                  />
                  ΜΕ ΣΥΣΚΕΥΑΣΙΑ / ΚΟΥΤΙ
                </label>
              </div>
            </Row>
          )}

          {/* Visual divider before ΕΝΗΜΕΡΩΣΗ ΠΕΛΑΤΗ (pickup only) */}
          {type === "pickup" && (
            <div
              className="border-t-2 border-dashed border-ink/15 my-2"
              data-testid="dialog-notify-divider"
              aria-hidden
            />
          )}

          {/* ΕΡΓΑΣΙΕΣ ΠΟΥ ΕΓΙΝΑΝ — collapsible (pickup + task) */}
          {(type === "pickup" || type === "task") && (
            <div data-testid="dialog-work-done-section">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={workDoneOpen}
                  onChange={(e) => {
                    setWorkDoneOpen(e.target.checked);
                    if (!e.target.checked) setField("work_done", "");
                  }}
                  className="w-4 h-4 accent-ink"
                  data-testid="dialog-work-done-toggle"
                />
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70">
                  ΕΡΓΑΣΙΕΣ ΠΟΥ ΕΓΙΝΑΝ
                </span>
              </label>
              {workDoneOpen && (
                <div className="mt-1.5">
                  <Textarea
                    value={data.work_done}
                    onChange={(e) => setField("work_done", e.target.value)}
                    rows={3}
                    autoFocus
                    className={`rounded-xl border-black/10 focus-visible:ring-ink/20 resize-none font-arial transition-colors ${
                      data.work_done.trim()
                        ? "bg-green-50/70 border-green-200"
                        : "bg-white/40"
                    }`}
                    data-testid="dialog-work-done-input"
                    placeholder="ΣΥΜΠΛΗΡΩΣΕ ΟΤΑΝ ΟΛΟΚΛΗΡΩΘΕΙ Η ΕΡΓΑΣΙΑ..."
                  />
                </div>
              )}
            </div>
          )}

          {/* ΕΝΗΜΕΡΩΣΗ ΠΕΛΑΤΗ — collapsible (pickup + task) */}
          {(type === "pickup" || type === "task") && (
            <div data-testid="dialog-notify-section">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={notifyOpen}
                  onChange={(e) => {
                    setNotifyOpen(e.target.checked);
                    if (!e.target.checked) {
                      // Reset notify state when collapsed
                      setData((prev) => ({
                        ...prev,
                        notify_tel: false,
                        notify_mail: false,
                        notify_store: false,
                        notify_onsite: false,
                        notify_at: "",
                      }));
                    }
                  }}
                  className="w-4 h-4 accent-ink"
                  data-testid="dialog-notify-toggle"
                />
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70">
                  ΕΝΗΜΕΡΩΣΗ ΠΕΛΑΤΗ
                </span>
              </label>
              {notifyOpen && (
                <div
                  className="mt-1.5 rounded-xl border border-black/10 bg-white/40 p-2.5 space-y-1.5"
                  data-testid="dialog-notify-block"
                >
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      { key: "notify_tel", label: "ΤΗΛΕΦΩΝΟ" },
                      { key: "notify_mail", label: "MAIL" },
                      { key: "notify_store", label: "ΣΤΟ ΚΑΤΑΣΤΗΜΑ" },
                      { key: "notify_onsite", label: "ΣΤΟΝ ΧΩΡΟ ΤΟΥ" },
                    ].map((c) => {
                      const active = !!data[c.key];
                      return (
                        <label
                          key={c.key}
                          data-testid={`dialog-${c.key.replace("_", "-")}-label`}
                          className={`flex items-center gap-2 cursor-pointer text-[11px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-lg border transition-colors select-none ${
                            active
                              ? "bg-green-50 border-green-300 text-green-900"
                              : "bg-white/70 border-black/10 text-ink/65 hover:border-ink/30"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={(e) => {
                              const next = e.target.checked;
                              const nextAny =
                                (c.key === "notify_tel" ? next : !!data.notify_tel) ||
                                (c.key === "notify_mail" ? next : !!data.notify_mail) ||
                                (c.key === "notify_store" ? next : !!data.notify_store) ||
                                (c.key === "notify_onsite" ? next : !!data.notify_onsite);
                              setData((prev) => ({
                                ...prev,
                                [c.key]: next,
                                notify_at:
                                  nextAny && !prev.notify_at ? nowLocalISO() : prev.notify_at,
                              }));
                            }}
                            className="w-4 h-4 accent-green-700"
                            data-testid={`dialog-${c.key.replace("_", "-")}-check`}
                          />
                          {c.label}
                        </label>
                      );
                    })}
                  </div>

                  {(() => {
                    const anyNotify =
                      data.notify_tel ||
                      data.notify_mail ||
                      data.notify_store ||
                      data.notify_onsite;
                    return (
                      <div
                        className={`flex items-center gap-2 pt-2 border-t border-black/5 ${
                          !anyNotify ? "opacity-40" : ""
                        }`}
                      >
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/55">
                          ΗΜ/ΩΡΑ
                        </span>
                        <input
                          type="datetime-local"
                          disabled={!anyNotify}
                          value={data.notify_at}
                          onChange={(e) => setField("notify_at", e.target.value)}
                          data-testid="dialog-notify-at"
                          className={`flex-1 px-2 py-1.5 rounded-lg border bg-white text-sm font-arial outline-none transition-colors ${
                            anyNotify
                              ? "border-ink/15 focus:border-ink/50 text-ink"
                              : "border-black/5 text-ink/40 cursor-not-allowed"
                          }`}
                        />
                        {anyNotify && data.notify_at && (
                          <button
                            type="button"
                            onClick={() => setField("notify_at", nowLocalISO())}
                            className="text-[10px] uppercase tracking-wider font-bold text-ink/55 hover:text-ink px-2 py-1 rounded hover:bg-black/5"
                            data-testid="dialog-notify-now"
                            title="ΟΡΙΣΕ ΤΩΡΑ"
                          >
                            ΤΩΡΑ
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* CHARGE + DELIVERY (2 cols) */}
          <div className="grid grid-cols-2 gap-2">
            <Row label="ΧΡΕΩΣΗ (€)">
              <Input
                value={data.charge}
                onChange={(e) => setField("charge", e.target.value)}
                className="rounded-xl bg-white/60 border-black/10 focus-visible:ring-ink/20 font-arial font-bold"
                data-testid="dialog-charge-input"
                placeholder="0.00"
              />
            </Row>
            <Row label="ΠΑΡΑΔΟΣΗ">
              <Input
                type="date"
                value={data.delivery_date}
                onChange={(e) => setField("delivery_date", e.target.value)}
                className="rounded-xl bg-white/60 border-black/10 focus-visible:ring-ink/20 font-arial"
                data-testid="dialog-delivery-input"
              />
            </Row>
          </div>

          {/* CHANNELS — single select */}
          <Row label="ΠΗΓΗ ΕΠΙΚΟΙΝΩΝΙΑΣ *">
            <div className="flex flex-wrap gap-1.5">
              {channels.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() =>
                    setSelectedChannel((prev) => (prev === c.value ? "" : c.value))
                  }
                  className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-full border transition-all ${
                    selectedChannel === c.value
                      ? "border-ink bg-ink text-white"
                      : "border-black/10 text-ink/60 hover:border-ink/40"
                  }`}
                  data-testid={`dialog-channel-${c.value}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </Row>

          {/* ASSIGNED TO */}
          <Row label="ΚΑΤΑΧΩΡΗΣΗ ΑΠΟ *">
            <div className="flex flex-wrap gap-1.5">
              {people.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setAssignedTo(p === assignedTo ? "" : p)}
                  className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-full border transition-all ${
                    assignedTo === p
                      ? "border-ink bg-ink text-white"
                      : "border-black/10 text-ink/60 hover:border-ink/40"
                  }`}
                  data-testid={`dialog-assigned-${p}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </Row>

          <DialogFooter className="!justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider text-ink/70 hover:text-ink hover:bg-black/5 transition-colors"
              data-testid="dialog-cancel"
            >
              ΑΚΥΡΟ
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                canSubmit
                  ? "bg-ink text-white hover:bg-ink/85 active:scale-95"
                  : "bg-ink/20 text-white/70 cursor-not-allowed"
              }`}
              title={
                !canSubmit
                  ? "ΕΠΙΛΕΞΕ ΠΗΓΗ ΕΠΙΚΟΙΝΩΝΙΑΣ ΚΑΙ ΚΑΤΑΧΩΡΗΣΗ ΑΠΟ"
                  : ""
              }
              data-testid="dialog-submit"
            >
              {mode === "edit" ? "ΑΠΟΘΗΚΕΥΣΗ" : "ΚΑΤΑΧΩΡΗΣΗ"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60">
        {label}
      </Label>
      {children}
    </div>
  );
}

function OrderItemsEditor({ items, onChange }) {
  const update = (idx, patch) =>
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () =>
    onChange([...items, { text: "", qty: "", delivered: 0, done: false }]);

  return (
    <div className="space-y-1.5" data-testid="order-items-editor">
      {/* Header */}
      <div className="grid grid-cols-[60px_1fr_28px] gap-1.5 px-1">
        <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-ink/45 text-center">
          ΠΟΣ.
        </div>
        <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-ink/45">
          ΠΕΡΙΓΡΑΦΗ ΕΙΔΟΥΣ
        </div>
        <div />
      </div>
      {items.map((it, idx) => (
        <div
          key={idx}
          className="grid grid-cols-[60px_1fr_28px] gap-1.5 items-center"
          data-testid={`order-row-${idx}`}
        >
          <input
            type="number"
            min="1"
            value={it.qty}
            placeholder="1"
            onChange={(e) => {
              const raw = e.target.value;
              // Allow empty during editing; coerce only positive integers
              if (raw === "") {
                update(idx, { qty: "" });
              } else {
                const n = parseInt(raw, 10);
                update(idx, { qty: Number.isNaN(n) || n < 1 ? "" : n });
              }
            }}
            data-testid={`order-row-${idx}-qty`}
            className="rounded-xl bg-white/60 border border-black/10 focus:border-ink/40 outline-none font-arial text-sm tabular-nums text-center px-2 py-2"
          />
          <input
            type="text"
            value={it.text}
            onChange={(e) =>
              update(idx, { text: e.target.value.toUpperCase() })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (idx === items.length - 1 && (it.text || "").trim()) {
                  add();
                  // focus next row in microtask
                  setTimeout(() => {
                    const el = document.querySelector(
                      `[data-testid="order-row-${idx + 1}-text"]`
                    );
                    if (el) el.focus();
                  }, 0);
                }
              }
            }}
            placeholder="π.χ. ΤΟΝΕΡ HP 85A"
            data-testid={`order-row-${idx}-text`}
            className="rounded-xl bg-white/60 border border-black/10 focus:border-ink/40 outline-none font-arial text-sm px-3 py-2"
          />
          <button
            type="button"
            onClick={() => remove(idx)}
            disabled={items.length === 1}
            data-testid={`order-row-${idx}-remove`}
            className="w-7 h-7 rounded-full text-red-600/70 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
            title="ΑΦΑΙΡΕΣΗ"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid="order-row-add"
        className="mt-1 w-full px-3 py-1.5 rounded-xl border-2 border-dashed border-ink/20 text-ink/60 hover:border-ink/40 hover:text-ink/80 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors"
      >
        + ΠΡΟΣΘΗΚΗ ΓΡΑΜΜΗΣ
      </button>
    </div>
  );
}
